import { Divider, Stack, useMediaQuery, Theme } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useState, useEffect } from "react";

import { MonthGrid } from "./MonthGrid";

interface MonthRangeCalendarProps {
  value?: [Dayjs | null, Dayjs | null];
  minDate?: Dayjs;
  maxDate?: Dayjs;
  onChange?: (range: [Dayjs | null, Dayjs | null]) => void;
}

export const MonthRangeCalendar = ({
  value,
  minDate,
  maxDate,
  onChange,
}: MonthRangeCalendarProps) => {
  const [year, setYear] = useState(dayjs().year());
  const [direction, setDirection] = useState<"left" | "right">("left");
  const [pendingYearChange, setPendingYearChange] = useState<number | null>(
    null,
  );
  const [animating, setAnimating] = useState(false);
  const [hoveredMonth, setHoveredMonth] = useState<Dayjs | null>(null);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  const [start, end] = value ?? [null, null];

  useEffect(() => {
    if (pendingYearChange !== null) {
      const timeoutId = setTimeout(() => {
        setYear(pendingYearChange);
        setPendingYearChange(null);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [pendingYearChange, direction]);

  const handleMonthSelect = (month: Dayjs) => {
    const hasStart = Boolean(start);
    const hasEnd = Boolean(end);

    if (!hasStart || hasEnd) {
      onChange?.([month, null]);

      return;
    }

    if (month.isBefore(start)) {
      onChange?.([month, null]);
    } else {
      onChange?.([start, month]);
    }
  };

  const handlePreviousClick = () => {
    if (!animating) {
      setDirection("right");
      setAnimating(true);
      setPendingYearChange(year - 1);
    }
  };

  const handleNextClick = () => {
    if (!animating) {
      setDirection("left");
      setAnimating(true);
      setPendingYearChange(year + 1);
    }
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      divider={
        <Divider
          flexItem
          orientation={isMobile ? "horizontal" : "vertical"}
          sx={{ borderColor: (theme) => theme.palette.grey["300"] }}
        />
      }
      sx={{
        width: { sm: 560, md: 625 },
        height: { xs: 470, sm: 240 },
        overflow: "hidden",
      }}>
      <MonthGrid
        isNextHidden
        direction={direction}
        hoveredMonth={hoveredMonth}
        maxDate={maxDate}
        minDate={minDate}
        value={value}
        year={year}
        onAnimationEnd={setAnimating}
        onHoverMonth={setHoveredMonth}
        onLeaveMonth={() => setHoveredMonth(null)}
        onNextClick={handleNextClick}
        onPreviousClick={handlePreviousClick}
        onSelectMonth={handleMonthSelect}
      />
      <MonthGrid
        isPreviousHidden
        direction={direction}
        hoveredMonth={hoveredMonth}
        maxDate={maxDate}
        minDate={minDate}
        value={value}
        year={year + 1}
        onAnimationEnd={setAnimating}
        onHoverMonth={setHoveredMonth}
        onLeaveMonth={() => setHoveredMonth(null)}
        onNextClick={handleNextClick}
        onPreviousClick={handlePreviousClick}
        onSelectMonth={handleMonthSelect}
      />
    </Stack>
  );
};
